import { Helmet } from "react-helmet-async";
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Lock, ArrowRight } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await register(email, password, name, 'practitioner');
      navigate(user.role === 'practitioner' ? '/my-studio' : '/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <Helmet><title>Register | Go Freestyle Studios</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="px-6 py-6">
        <Link to="/" className="flex items-center" data-testid="brand-link">
          <img src="/logo.webp" alt="Go Freestyle Studios" className="h-[52px]" style={{filter:'contrast(1.15) brightness(0.95)'}} />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] tracking-tight" data-testid="register-heading">
              Create account
            </h1>
            <p className="mt-2 text-sm text-[#5C5C55]">Join Go Freestyle Studios</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#F5E6E6] text-[#8A3D3D] text-sm rounded-xl" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Full Name</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required
                  className="pl-11 bg-[#F2F0EB] border-transparent rounded-xl px-4 py-3 text-base focus:bg-white focus:border-[#3A4035] focus:ring-1 focus:ring-[#3A4035] h-12 placeholder:text-[#8A8B85]"
                  data-testid="register-name-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required
                  className="pl-11 bg-[#F2F0EB] border-transparent rounded-xl px-4 py-3 text-base focus:bg-white focus:border-[#3A4035] focus:ring-1 focus:ring-[#3A4035] h-12 placeholder:text-[#8A8B85]"
                  data-testid="register-email-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Choose a password" required
                  className="pl-11 bg-[#F2F0EB] border-transparent rounded-xl px-4 py-3 text-base focus:bg-white focus:border-[#3A4035] focus:ring-1 focus:ring-[#3A4035] h-12 placeholder:text-[#8A8B85]"
                  data-testid="register-password-input" />
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full bg-[#3A4035] text-white rounded-full px-6 py-3 h-12 font-medium hover:bg-[#2B3027] transition-colors"
              data-testid="register-submit-button">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Create account <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} /></>}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-[#5C5C55]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#3A4035] font-medium hover:underline" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
